import * as React from 'react'
import { clsx } from 'clsx'

import type { IconName } from './iconName'
import type { IconColorName } from './constants'
import { PickerIcon } from './PickerIcon'
import { useIcons } from './useIcons'

import styles from './IconPicker.module.css'

type IconTileProps = {
  /** Curated glyph to render. */
  icon: IconName
  /** Tint family driving the background, border, and ink. */
  color: IconColorName
  /**
   * `tile` draws a tinted, bordered tile (matching the picker trigger); `plain`
   * renders the bare glyph in the tint ink with no chrome.
   * @default 'tile'
   */
  appearance?: 'tile' | 'plain'
  /**
   * `small` is the 24px sidebar size; `medium` matches the picker trigger box.
   * @default 'small'
   */
  size?: 'small' | 'medium'
} & React.HTMLAttributes<HTMLSpanElement>

/** Glyph pixel size per box size. */
const GLYPH_SIZE: Record<NonNullable<IconTileProps['size']>, number> = {
  small: 16,
  medium: 20,
}

/**
 * A standalone tinted icon tile: the picker's selection rendered outside the
 * picker (e.g. next to a saved view in the sidebar). Always decorative — it is
 * hidden from assistive tech, so consumers must provide an adjacent text label.
 */
const IconTile = React.forwardRef<HTMLSpanElement, IconTileProps>(
  (
    { icon, color, appearance = 'tile', size = 'small', className, ...props },
    ref,
  ) => {
    const { icons } = useIcons()

    return (
      <span
        ref={ref}
        // Decorative: the glyph and tint duplicate meaning carried by an
        // adjacent visible label, so it stays out of the accessibility tree.
        aria-hidden
        className={clsx(
          styles.iconTile,
          appearance === 'plain' ? styles.iconTilePlain : styles.iconTileFilled,
          size === 'medium' ? styles.iconTileMedium : styles.iconTileSmall,
          // Every IconColorName has a matching CSS tint modifier class.
          styles[color],
          className,
        )}
        {...props}
      >
        <PickerIcon icon={icon} icons={icons} size={GLYPH_SIZE[size]} />
      </span>
    )
  },
)

IconTile.displayName = 'IconTile'

export { IconTile }
export type { IconTileProps }
